import { Controller } from "@hotwired/stimulus";
import { PriceCalculator } from "./price_calculator.js";
import { createConstructorState } from "./constructor_state.js";
import { buildConstructorItems } from "./constructor_items.js";
import { validateConstructorState, validateModalContact } from "./constructor_validation.js";
import { buildConstructorViewModel } from "./constructor_presenter.js";
import { buildConstructorPayload } from "./constructor_payload.js";
import {
  getConstructorDefaults,
  getDrawingDefinitions,
  getProjectTypeKeys,
  isValidProjectType,
  normalizeProjectCategory,
  shouldShowSpecification,
} from "./constructor_pricing.js";

const DRAWING_ID_OVERRIDES = Object.freeze({
  montage: "p3montage",
  demolition: "p3demolition",
  custom_furniture_drawings: "p3furnitureDrawings",
  wall_elevations: "p3razvortky",
});

export default class extends Controller {
  static targets = [
    "form",
    "locationHidden",
    "locationError",
    "projectType",
    "panels",
    "room",
    "partialCheck",
    "planningCheck",
    "planningVariants",
    "partialRow",
    "partialArea",
    "allDraw",
    "singleDraw",
    "singleDrawList",
    "razvortkyCheck",
    "razvortkyRow",
    "razvortkyArea",
    "specificationPanel",
    "specificationCheck",
    "priceRows",
    "priceTotal",
    "calcBtn",
    "submitBtn",
  ];

  static values = {
    currency: { type: String, default: "uah" },
    eurToPln: { type: Number, default: 4.3 },
    eurToUsd: { type: Number, default: 1.0 },
    symbols: { type: Object, default: { uah: "грн", pln: "zł", eur: "€", usd: "$" } },
    sendingText: { type: String, default: "…" },
    areaUnit: { type: String, default: "м²" },
    estimatedCostLabel: { type: String, default: "Орієнтовна вартість:" },
    errorSendLabel: { type: String, default: "Помилка відправки" },
    errorNetworkLabel: { type: String, default: "Мережна помилка" },
    errorUnknownLabel: { type: String, default: "Невідома помилка" },
    nameRequiredLabel: { type: String, default: "Ім'я обов'язкове" },
    phoneInvalidLabel: { type: String, default: "Невірний номер телефону" },
    projectTypeLabels: { type: Object, default: {} },
    drawingLabels: { type: Object, default: {} },
  };

  connect() {
    this.calculator = new PriceCalculator({
      eurToPlnRate: this.eurToPlnValue,
      eurToUsdRate: this.eurToUsdValue,
      symbols: this.symbolsValue,
    });

    this._onFormChange = () => {
      if (this.hasFormTarget) this.formTarget.classList.add("was-validated");
      this.refresh();
    };
    this._onFormFocusOut = (event) => {
      const tagName = event.target?.tagName;
      if (tagName && ["INPUT", "SELECT", "TEXTAREA"].includes(tagName)) {
        if (this.hasFormTarget) this.formTarget.classList.add("was-validated");
        this.refresh();
      }
    };
    this._onDocumentPointerDown = (event) => {
      if (!event.target.closest(".cstr-help")) this._closeHelpTooltips();
    };

    this.element.addEventListener("change", this._onFormChange);
    this.element.addEventListener("focusout", this._onFormFocusOut);
    document.addEventListener("pointerdown", this._onDocumentPointerDown);

    this._isSending = false;
    this._pendingSnapshot = null;
    this._printShell = null;
    this._afterPrintHandler = null;
    this._partialAreaLabel =
      this.element.querySelector("label[for='razvortkyArea']")?.textContent?.trim() || "";
    this._areaLimitErrorLabel =
      this.element.querySelector("#razvortkyArea + .invalid-feedback")?.textContent?.trim() || "";
    this._wallElevationsHint =
      this.element.querySelector("#p3razvortky + label .visually-hidden")?.textContent?.trim() || "";
    this._renderedDrawingCategoryKey = null;
    this._lastAutofilledArea = "";
    this._setupFeedbackUi();
    this._setupModalUi();
    this.refresh();
    this._lastProjectTypeValue = this.hasProjectTypeTarget ? this.projectTypeTarget.value : "";
  }

  disconnect() {
    this.element.removeEventListener("change", this._onFormChange);
    this.element.removeEventListener("focusout", this._onFormFocusOut);
    document.removeEventListener("pointerdown", this._onDocumentPointerDown);

    if (this.modalForm) this.modalForm.removeEventListener("submit", this._onModalSubmit);
    if (this._modalNameInput) {
      this._modalNameInput.removeEventListener("blur", this._onModalFieldChange);
      this._modalNameInput.removeEventListener("input", this._onModalFieldChange);
    }
    if (this._modalPhoneInput) {
      this._modalPhoneInput.removeEventListener("input", this._onModalFieldChange);
      this._modalPhoneInput.removeEventListener("blur", this._onModalFieldChange);
    }

    this._cleanupInvoicePrint();
  }

  currencyValueChanged() {
    if (!this.calculator) return;
    this.refresh();
  }

  recalc() {
    this.refresh();
  }

  validateAreas() {
    this.refresh();
  }

  printInvoice(event) {
    event.preventDefault();

    if (this.hasFormTarget) this.formTarget.classList.add("was-validated");
    const snapshot = this.refresh();

    const isDomValid = this.hasFormTarget ? this.formTarget.checkValidity() : true;
    if (!isDomValid || !snapshot.validation.isBusinessValid || snapshot.validation.locationMissing) {
      return;
    }

    this._setInvoiceDocumentTitle();
    this._renderInvoiceForPrint(snapshot);
    this._armInvoicePrintCleanup();

    try {
      window.print();
    } catch (error) {
      this._cleanupInvoicePrint();
      throw error;
    }
  }

  selectLocation(event) {
    const select = event.currentTarget;
    const option = select.options[select.selectedIndex];

    this.currencyValue = option?.dataset?.currency || "eur";
    if (this.hasLocationErrorTarget) this.locationErrorTarget.classList.add("d-none");
    this.refresh();
  }

  onCategoryChange() {
    const nextCategoryKey = this._selectedProjectCategoryKey();

    this._syncCategoryDrivenControls({ force: true });
    this._resetConstructorControls(nextCategoryKey);
    this._lastProjectTypeValue = this.hasProjectTypeTarget ? this.projectTypeTarget.value : "";
    this.refresh();
  }

  onTypeChange() {
    const nextType = this.hasProjectTypeTarget ? this.projectTypeTarget.value : "";

    if (this._lastProjectTypeValue === "constructor" && nextType !== "constructor") {
      this._resetConstructorControls(this._selectedProjectCategoryKey());
    }

    this._lastProjectTypeValue = nextType;
    this.refresh();
  }

  onPartialChange() {
    this.refresh();
  }

  onPlanningToggle() {
    if (!this.hasPlanningCheckTarget || !this.hasPlanningVariantsTarget) {
      this.refresh();
      return;
    }

    if (!this.planningCheckTarget.checked) {
      this.planningVariantsTarget.value = "0";
    }

    this._syncPlanningControls();
    this.refresh();
  }

  onPlanningQuantityChange() {
    this._normalizePlanningVariantsValue();
    this.refresh();
  }

  onAllDrawChange() {
    if (!this.hasAllDrawTarget || !this.hasSingleDrawTarget) {
      this.refresh();
      return;
    }

    const checked = this.allDrawTarget.checked;
    this.allDrawTarget.indeterminate = false;
    this.singleDrawTargets.forEach((element) => {
      element.checked = checked;
    });
    this.refresh();
  }

  onSingleDrawChange() {
    this._syncAllDrawToggle();
    this.refresh();
  }

  onRazvortkyChange() {
    this._syncAllDrawToggle();
    this.refresh();
  }

  syncAreaOnBlur() {
    const areaValue = parseFloat(this.roomTarget.value) || 0;

    if (areaValue > 0) {
      if (this.hasPartialAreaTarget && !this.partialAreaTarget.value) {
        this.partialAreaTarget.value = String(areaValue);
      }
      if (this.hasRazvortkyAreaTarget && !this.razvortkyAreaTarget.value) {
        this.razvortkyAreaTarget.value = String(areaValue);
      }
    }

    this.refresh();
  }

  toggleHelp(event) {
    event.preventDefault();
    event.stopPropagation();

    const trigger = event.currentTarget;
    const isExpanded = trigger.getAttribute("aria-expanded") === "true";

    this._closeHelpTooltips();

    if (!isExpanded) {
      trigger.setAttribute("aria-expanded", "true");
      trigger.focus();
    }
  }

  closeHelp(event) {
    this._collapseHelpTrigger(event.currentTarget);
  }

  closeHelpOnBlur(event) {
    const nextFocusedElement = event.relatedTarget;
    if (nextFocusedElement && event.currentTarget.contains(nextFocusedElement)) return;
    this._collapseHelpTrigger(event.currentTarget);
  }

  refresh() {
    this._syncCategoryDrivenControls();
    this._prefillAreaInputsFromRoom();
    this._syncPrerequisiteControls();
    this._syncPlanningControls();
    this._syncAllDrawToggle();

    const snapshot = this._buildSnapshot();
    this._render(snapshot);
    this._lastSnapshot = snapshot;

    return snapshot;
  }

  submit(event) {
    event.preventDefault();

    if (this.hasFormTarget) this.formTarget.classList.add("was-validated");
    const snapshot = this.refresh();

    const isDomValid = this.hasFormTarget ? this.formTarget.checkValidity() : true;
    if (!isDomValid || !snapshot.validation.isBusinessValid || snapshot.validation.locationMissing) {
      return;
    }

    this._pendingSnapshot = snapshot;
    if (this.modalElement) {
      this._resetModalState();
      this._modalInstance = new window.bootstrap.Modal(this.modalElement);
      this._modalInstance.show();
      return;
    }

    const payload = buildConstructorPayload({
      state: snapshot.state,
      quote: snapshot.quote,
      estimatedCostLabel: this.estimatedCostLabelValue,
      clientName: "",
      phoneNumber: "",
    });
    this._sendForm(payload);
  }

  async _onModalSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const modalValidation = this._readModalValidation();
    this._renderModalValidation(modalValidation, { showErrors: true });
    form.classList.add("was-validated");

    if (!form.checkValidity() || !modalValidation.isValid) return;

    const snapshot = this._pendingSnapshot || this.refresh();
    const payload = buildConstructorPayload({
      state: snapshot.state,
      quote: snapshot.quote,
      estimatedCostLabel: this.estimatedCostLabelValue,
      clientName: modalValidation.name,
      phoneNumber: modalValidation.phone,
    });

    try {
      if (this._modalServerErrorEl) {
        this._modalServerErrorEl.classList.add("d-none");
        this._modalServerErrorEl.textContent = "";
      }
    } catch (error) {}

    await this._sendForm(payload);
    this._pendingSnapshot = null;

    try {
      form.classList.remove("was-validated");
    } catch (error) {}
  }

  _buildSnapshot() {
    const state = createConstructorState(this._readStateFromDom());
    const validation = validateConstructorState(state);
    const items = buildConstructorItems(state);
    const quote = this.calculator.calculate(state.currency, items, {
      projectType: state.projectType,
      projectCategoryKey: state.projectCategoryKey,
    });

    return {
      state,
      validation,
      items,
      quote,
      viewModel: buildConstructorViewModel(state, validation, quote, {
        htmlValid: this.hasFormTarget ? this.formTarget.checkValidity() : true,
        projectCategoryKey: state.projectCategoryKey,
        projectTypeOptions: this._projectTypeOptions(),
        symbols: this.symbolsValue,
        areaUnit: this.areaUnitValue,
        eurToPlnRate: this.eurToPlnValue,
        eurToUsdRate: this.eurToUsdValue,
      }),
    };
  }

  _readStateFromDom() {
    const selectedProjectTypeOption = this.hasProjectTypeTarget
      ? this.projectTypeTarget.options[this.projectTypeTarget.selectedIndex]
      : null;
    const selectedProjectCategoryInput = this._selectedProjectCategoryInput();

    return {
      area: this.hasRoomTarget ? this.roomTarget.value : "",
      partialArea: this.hasPartialAreaTarget ? this.partialAreaTarget.value : "",
      razvortkyArea: this.hasRazvortkyAreaTarget ? this.razvortkyAreaTarget.value : "",
      location: this.hasLocationHiddenTarget ? this.locationHiddenTarget.value : "",
      currency: this.currencyValue,
      projectCategory: selectedProjectCategoryInput?.value || "",
      projectCategoryKey: this._selectedProjectCategoryKey(),
      projectType: selectedProjectTypeOption?.value || "",
      projectTypeLabel:
        selectedProjectTypeOption?.dataset?.label ||
        selectedProjectTypeOption?.textContent?.split("—")[0].trim() ||
        "",
      planningSelection: this.hasPlanningCheckTarget
        ? {
            checked: this.planningCheckTarget.checked,
            label: this.planningCheckTarget.dataset.label,
            priceKey: this.planningCheckTarget.dataset.priceKey,
          }
        : {},
      planningVariants: this.hasPlanningVariantsTarget ? this.planningVariantsTarget.value : "0",
      specificationSelection:
        this.hasSpecificationCheckTarget && !this.specificationCheckTarget.disabled
          ? {
              checked: this.specificationCheckTarget.checked,
              label: this.specificationCheckTarget.dataset.label,
              priceKey: this.specificationCheckTarget.dataset.priceKey,
            }
          : {},
      partialSelection: this.hasPartialCheckTarget
        ? {
            checked: this.partialCheckTarget.checked,
            label: this.partialCheckTarget.dataset.label,
            priceKey: this.partialCheckTarget.dataset.priceKey,
          }
        : {},
      singleDraws: this.hasSingleDrawTarget
        ? this.singleDrawTargets.map((element) => ({
            checked: element.checked,
            label: element.dataset.label,
            priceKey: element.dataset.priceKey,
            isRazvortky: element.dataset.razvortky === "true",
          }))
        : [],
      razvortkySelection: this.hasRazvortkyCheckTarget
        ? {
            checked: this.razvortkyCheckTarget.checked,
            label: this.razvortkyCheckTarget.dataset.label,
            priceKey: this.razvortkyCheckTarget.dataset.priceKey,
          }
        : {},
    };
  }

  _render(snapshot) {
    const { viewModel, validation } = snapshot;

    this._renderProjectTypeOptions(viewModel.optionLabels);
    this._renderVisibility(viewModel);
    this._renderPriceSummary(viewModel);
    this._renderValidation(validation, viewModel);
    this._renderActionButtons(viewModel);
  }

  _renderProjectTypeOptions(optionLabels) {
    if (!this.hasProjectTypeTarget) return;

    optionLabels.forEach((optionLabel) => {
      const option = Array.from(this.projectTypeTarget.options).find(
        (candidate) => candidate.value === optionLabel.value
      );
      if (option) option.textContent = optionLabel.text;
    });
  }

  _renderVisibility(viewModel) {
    if (this.hasLocationHiddenTarget) this.locationHiddenTarget.disabled = viewModel.locationDisabled;
    if (this.hasProjectTypeTarget) this.projectTypeTarget.disabled = viewModel.projectTypeDisabled;
    if (this.hasPanelsTarget) this.panelsTarget.hidden = viewModel.panelsHidden;
    if (this.hasPartialRowTarget) this.partialRowTarget.classList.toggle("show", viewModel.partialRowShown);
    if (this.hasRazvortkyRowTarget) this.razvortkyRowTarget.classList.toggle("show", viewModel.razvortkyRowShown);
  }

  _renderPriceSummary(viewModel) {
    if (this.hasPriceRowsTarget) this.priceRowsTarget.innerHTML = viewModel.priceRowsHtml;
    if (this.hasPriceTotalTarget) this.priceTotalTarget.textContent = viewModel.priceTotalText;
  }

  _renderValidation(validation, viewModel) {
    this._setFieldInvalid(this.hasPartialAreaTarget ? this.partialAreaTarget : null, validation.partialAreaInvalid);
    this._setCheckboxInvalid(this.hasPartialCheckTarget ? this.partialCheckTarget : null, validation.partialAreaInvalid);

    this._setFieldInvalid(
      this.hasRazvortkyAreaTarget ? this.razvortkyAreaTarget : null,
      validation.razvortkyAreaInvalid
    );
    this._setCheckboxInvalid(
      this.hasRazvortkyCheckTarget ? this.razvortkyCheckTarget : null,
      validation.razvortkyAreaInvalid
    );

    if (this.hasSingleDrawTarget) {
      this.singleDrawTargets.forEach((element) => {
        if (element.dataset.razvortky !== "true") element.classList.remove("is-invalid");
      });
    }

    if (this.hasAllDrawTarget) {
      const hasInvalidChild = validation.razvortkyAreaInvalid;
      this._setCheckboxInvalid(this.allDrawTarget, hasInvalidChild);
    }

    if (this.hasLocationErrorTarget) {
      const showLocationError =
        validation.locationMissing &&
        this.hasFormTarget &&
        this.formTarget.classList.contains("was-validated") &&
        !viewModel.locationDisabled;

      this.locationErrorTarget.classList.toggle("d-none", !showLocationError);
    }
  }

  _renderActionButtons(viewModel) {
    if (this._isSending) return;

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = viewModel.actionButtonsDisabled;
    if (this.hasCalcBtnTarget) this.calcBtnTarget.disabled = viewModel.actionButtonsDisabled;
  }

  _setFieldInvalid(element, invalid) {
    if (!element) return;

    element.setCustomValidity(invalid ? "invalid" : "");
    element.classList.toggle("is-invalid", invalid);
  }

  _setCheckboxInvalid(element, invalid) {
    if (!element) return;
    element.classList.toggle("is-invalid", invalid);
  }

  _selectedProjectCategoryInput() {
    return this.element.querySelector("input[name='projectCategory']:checked");
  }

  _selectedProjectCategoryKey() {
    return normalizeProjectCategory(
      this._selectedProjectCategoryInput()?.dataset?.categoryKey || this._selectedProjectCategoryInput()?.value || ""
    );
  }

  _syncCategoryDrivenControls({ force = false } = {}) {
    const categoryKey = this._selectedProjectCategoryKey();
    this._syncProjectTypeOptions(categoryKey, { force });
    this._syncSingleDrawRows(categoryKey, { force });
    this._syncSpecificationVisibility(categoryKey);
  }

  _syncProjectTypeOptions(categoryKey, { force = false } = {}) {
    if (!this.hasProjectTypeTarget) return;

    const currentValue = this.projectTypeTarget.value;
    const nextOptions = this._baseProjectTypeOptions(categoryKey);
    const currentOptions = this._projectTypeOptions();
    const needsRebuild =
      force ||
      currentOptions.length !== nextOptions.length ||
      currentOptions.some((option, index) => {
        const candidate = nextOptions[index];
        return option.value !== candidate.value || option.label !== candidate.label;
      });

    if (needsRebuild) {
      this.projectTypeTarget.innerHTML = nextOptions
        .map(
          ({ value, label }) =>
            `<option value="${this._escapeHtml(value)}" ${value ? "" : "disabled"} data-label="${this._escapeHtml(
              label
            )}">${this._escapeHtml(label)}</option>`
        )
        .join("");
    }

    this.projectTypeTarget.value = isValidProjectType(categoryKey, currentValue) ? currentValue : "";
  }

  _baseProjectTypeOptions(categoryKey) {
    const labels = this.projectTypeLabelsValue?.[categoryKey] || {};
    const placeholder =
      this.hasProjectTypeTarget && this.projectTypeTarget.options.length > 0
        ? this.projectTypeTarget.options[0]?.dataset?.label || this.projectTypeTarget.options[0]?.textContent?.trim() || ""
        : "";

    return [
      { value: "", label: placeholder },
      ...getProjectTypeKeys(categoryKey).map((key) => ({
        value: key,
        label: labels[key] || key,
      })),
    ];
  }

  _syncSingleDrawRows(categoryKey, { force = false } = {}) {
    if (!this.hasSingleDrawListTarget) return;
    if (!force && this._renderedDrawingCategoryKey === categoryKey) return;

    const defaults = getConstructorDefaults(categoryKey);
    this.singleDrawListTarget.innerHTML = getDrawingDefinitions(categoryKey)
      .map((definition) => this._buildSingleDrawMarkup(definition, categoryKey, defaults))
      .join("");
    this._renderedDrawingCategoryKey = categoryKey;
  }

  _buildSingleDrawMarkup(definition, categoryKey, defaults) {
    const checked = this._isDefaultDrawingSelected(definition.key, defaults) ? ' checked' : '';
    const id = this._drawingInputId(definition.key);
    const label = this._escapeHtml(this._resolveDrawingLabel(definition.key, categoryKey));
    const action = definition.isRazvortky ? 'change->constructor#onRazvortkyChange' : 'change->constructor#onSingleDrawChange';
    const checkboxTargets = definition.isRazvortky ? 'singleDraw razvortkyCheck' : 'singleDraw';
    const hint = definition.isRazvortky && this._wallElevationsHint
      ? `<span class="visually-hidden">${this._escapeHtml(this._wallElevationsHint)}</span>`
      : '';

    const areaRow = definition.isRazvortky
      ? `
        <div class="partial-row" data-constructor-target="razvortkyRow">
          <label for="razvortkyArea" class="text-gray" style="font-size:.8rem;white-space:nowrap;">${this._escapeHtml(
            this._partialAreaLabel
          )}</label>
          <input type="number" id="razvortkyArea"
            class="form-control rounded-0 py-1 px-2" min="1" placeholder="м²"
            inputmode="decimal" step="0.01" style="max-width:90px;font-size:.8rem;"
            data-constructor-target="razvortkyArea"
            data-action="input->constructor#recalc change->constructor#validateAreas blur->constructor#validateAreas">
          <div class="invalid-feedback">${this._escapeHtml(this._areaLimitErrorLabel)}</div>
        </div>`
      : '';

    return `
      <div class="form-check">
        <input class="form-check-input" type="checkbox"${checked}
          id="${this._escapeHtml(id)}" data-constructor-target="${checkboxTargets}"
          data-drawing-key="${this._escapeHtml(definition.key)}"
          ${definition.isRazvortky ? 'data-razvortky="true"' : ''} data-price-key="${this._escapeHtml(
            definition.priceKey
          )}"
          data-label="${label}"
          data-action="${action}">
        <label class="form-check-label" for="${this._escapeHtml(id)}" style="font-size:.82rem;">${label}${hint}</label>
      </div>${areaRow}
    `;
  }

  _isDefaultDrawingSelected(key, defaults) {
    return defaults.selectedDrawKeys === "all" || defaults.selectedDrawKeys.includes(key);
  }

  _drawingInputId(key) {
    return DRAWING_ID_OVERRIDES[key] || `p3-${key}`;
  }

  _resolveDrawingLabel(key, categoryKey) {
    return this.drawingLabelsValue?.[categoryKey]?.[key] || key;
  }

  _syncSpecificationVisibility(categoryKey) {
    const showSpecification = shouldShowSpecification(categoryKey);

    if (this.hasSpecificationPanelTarget) {
      this.specificationPanelTarget.hidden = !showSpecification;
      if (showSpecification) this.specificationPanelTarget.open = true;
    }

    if (this.hasSpecificationCheckTarget) {
      this.specificationCheckTarget.disabled = !showSpecification;
      if (!showSpecification) this.specificationCheckTarget.checked = false;
    }
  }

  _prefillAreaInputsFromRoom() {
    if (!this.hasRoomTarget) return;

    const parsedArea = parseFloat(this.roomTarget.value);
    if (!(parsedArea > 0)) {
      this._lastAutofilledArea = "";
      return;
    }

    const normalizedArea = String(parsedArea);
    const shouldAutofill = (element) => element && (element.value.trim() === "" || element.value === this._lastAutofilledArea);

    if (this.hasPartialAreaTarget && shouldAutofill(this.partialAreaTarget)) {
      this.partialAreaTarget.value = normalizedArea;
    }
    if (this.hasRazvortkyAreaTarget && shouldAutofill(this.razvortkyAreaTarget)) {
      this.razvortkyAreaTarget.value = normalizedArea;
    }

    this._lastAutofilledArea = normalizedArea;
  }

  _syncPrerequisiteControls() {
    const hasCategory = Boolean(this._selectedProjectCategoryInput());
    const areaValue = this.hasRoomTarget ? parseFloat(this.roomTarget.value) || 0 : 0;
    const hasArea = areaValue > 0;

    if (this.hasRoomTarget) this.roomTarget.disabled = false;

    if (this.hasLocationHiddenTarget) {
      const wasDisabled = this.locationHiddenTarget.disabled;
      this.locationHiddenTarget.disabled = !hasArea;

      if (!hasArea && !wasDisabled) {
        try {
          this.locationHiddenTarget.selectedIndex = 0;
        } catch (error) {}

        if (this.hasLocationErrorTarget) this.locationErrorTarget.classList.add("d-none");
      }
    }

    if (this.hasProjectTypeTarget) this.projectTypeTarget.disabled = !(hasCategory && hasArea);
    if (this.hasPanelsTarget && this.hasProjectTypeTarget) {
      this.panelsTarget.hidden = this.projectTypeTarget.disabled || this.projectTypeTarget.value !== "constructor";
    }
  }

  _syncAllDrawToggle() {
    if (!this.hasAllDrawTarget || !this.hasSingleDrawTarget) return;

    const checkedCount = this.singleDrawTargets.filter((element) => element.checked).length;
    this.allDrawTarget.checked = checkedCount === this.singleDrawTargets.length;
    this.allDrawTarget.indeterminate = checkedCount > 0 && checkedCount < this.singleDrawTargets.length;
  }

  _syncPlanningControls() {
    if (!this.hasPlanningVariantsTarget) return;

    const enabled = this.hasPlanningCheckTarget && this.planningCheckTarget.checked;
    this.planningVariantsTarget.disabled = !enabled;

    if (!enabled) {
      this.planningVariantsTarget.value = "0";
      return;
    }

    this._normalizePlanningVariantsValue();
  }

  _normalizePlanningVariantsValue() {
    if (!this.hasPlanningVariantsTarget) return;

    const normalizedValue = Math.max(0, Math.floor(parseFloat(this.planningVariantsTarget.value) || 0));
    this.planningVariantsTarget.value = String(normalizedValue);
  }

  _resetConstructorControls(categoryKey = this._selectedProjectCategoryKey()) {
    const defaults = getConstructorDefaults(categoryKey);

    if (this.hasPlanningCheckTarget) this.planningCheckTarget.checked = defaults.planningChecked;
    if (this.hasPlanningVariantsTarget) {
      this.planningVariantsTarget.value = String(defaults.planningVariants);
      this.planningVariantsTarget.disabled = !defaults.planningChecked;
    }

    if (this.hasPartialCheckTarget) this.partialCheckTarget.checked = defaults.partialChecked;
    if (this.hasPartialAreaTarget) this.partialAreaTarget.value = "";

    if (this.hasSingleDrawTarget) {
      this.singleDrawTargets.forEach((element) => {
        element.checked = this._isDefaultDrawingSelected(element.dataset.drawingKey || "", defaults);
      });
    }

    if (this.hasRazvortkyAreaTarget) this.razvortkyAreaTarget.value = "";

    this._syncSpecificationVisibility(categoryKey);
    if (this.hasSpecificationCheckTarget && !this.specificationCheckTarget.disabled) {
      this.specificationCheckTarget.checked = defaults.specificationChecked;
    }

    this._syncPlanningControls();
    this._syncAllDrawToggle();
  }

  _projectTypeOptions() {
    if (!this.hasProjectTypeTarget) return [];

    return Array.from(this.projectTypeTarget.options).map((option) => ({
      value: option.value,
      label: option.dataset.label || option.textContent.trim(),
    }));
  }

  _setupFeedbackUi() {
    try {
      this._successToast = new window.bootstrap.Toast(this.element.querySelector(".success_msg"));
      this._errorToast = new window.bootstrap.Toast(this.element.querySelector(".error_msg"));
    } catch (error) {}
  }

  _setupModalUi() {
    this.modalElement = this.element.querySelector("#requestModal");
    this.modalForm = this.modalElement ? this.modalElement.querySelector("form") : null;
    this._modalNameInput = this.modalElement?.querySelector('input[name="clientName"]') || null;
    this._modalPhoneInput = this.modalElement?.querySelector('input[name="phoneNumber"]') || null;
    this._modalNameErrorEl = this.modalElement?.querySelector("#clientNameError") || null;
    this._modalPhoneErrorEl = this.modalElement?.querySelector("#phoneError") || null;
    this._modalServerErrorEl = this.modalElement?.querySelector("#modalServerError") || null;
    this._modalSubmitBtn = this.modalElement?.querySelector('button[type="submit"]') || null;

    this._onModalSubmit = this._onModalSubmit.bind(this);
    this._onModalFieldChange = this._onModalFieldChange.bind(this);

    if (this.modalForm) this.modalForm.addEventListener("submit", this._onModalSubmit);
    if (this._modalNameInput) {
      this._modalNameInput.addEventListener("blur", this._onModalFieldChange);
      this._modalNameInput.addEventListener("input", this._onModalFieldChange);
    }
    if (this._modalPhoneInput) {
      this._modalPhoneInput.addEventListener("input", this._onModalFieldChange);
      this._modalPhoneInput.addEventListener("blur", this._onModalFieldChange);
    }

    if (this._modalSubmitBtn) this._modalSubmitBtn.disabled = true;
  }

  _resetModalState() {
    if (!this.modalForm) return;

    try {
      this.modalForm.classList.remove("was-validated");
    } catch (error) {}

    if (this._modalNameInput) {
      this._modalNameInput.value = "";
      this._modalNameInput.classList.remove("is-invalid");
    }
    if (this._modalPhoneInput) {
      this._modalPhoneInput.value = "";
      this._modalPhoneInput.classList.remove("is-invalid");
      this._modalPhoneInput.setCustomValidity("");
    }
    if (this._modalNameErrorEl) {
      this._modalNameErrorEl.textContent = "";
      this._modalNameErrorEl.classList.add("d-none");
    }
    if (this._modalPhoneErrorEl) {
      this._modalPhoneErrorEl.textContent = "";
      this._modalPhoneErrorEl.classList.add("d-none");
    }
    if (this._modalServerErrorEl) {
      this._modalServerErrorEl.textContent = "";
      this._modalServerErrorEl.classList.add("d-none");
    }

    this._updateModalSubmitState();
  }

  _onModalFieldChange() {
    const validation = this._readModalValidation();
    this._renderModalValidation(validation, { showErrors: true });
    this._updateModalSubmitState();
  }

  _readModalValidation() {
    return validateModalContact({
      clientName: this._modalNameInput?.value || "",
      phoneNumber: this._modalPhoneInput?.value || "",
    });
  }

  _renderModalValidation(validation, { showErrors }) {
    if (this._modalNameInput) this._modalNameInput.classList.toggle("is-invalid", validation.nameInvalid && showErrors);
    if (this._modalPhoneInput) {
      this._modalPhoneInput.classList.toggle("is-invalid", validation.phoneInvalid && showErrors);
      this._modalPhoneInput.setCustomValidity(validation.phoneInvalid ? "invalid_phone" : "");
    }

    if (this._modalNameErrorEl) {
      this._modalNameErrorEl.textContent = validation.nameInvalid ? this.nameRequiredLabelValue : "";
      this._modalNameErrorEl.classList.toggle("d-none", !(validation.nameInvalid && showErrors));
    }
    if (this._modalPhoneErrorEl) {
      this._modalPhoneErrorEl.textContent = validation.phoneInvalid ? this.phoneInvalidLabelValue : "";
      this._modalPhoneErrorEl.classList.toggle("d-none", !(validation.phoneInvalid && showErrors));
    }
  }

  _updateModalSubmitState() {
    if (!this._modalSubmitBtn) return;

    const validation = this._readModalValidation();
    this._modalSubmitBtn.disabled = this._isSending || !validation.isValid;
  }

  async _sendForm(payload) {
    const mainButton = this.hasSubmitBtnTarget ? this.submitBtnTarget : null;
    const modalButton = this._modalSubmitBtn;
    const originalMainText = mainButton ? mainButton.dataset.label || mainButton.textContent : null;
    const originalModalText = modalButton ? modalButton.dataset.label || modalButton.textContent : null;

    this._isSending = true;

    try {
      if (mainButton) {
        mainButton.textContent = this.sendingTextValue;
        mainButton.disabled = true;
      }
      if (modalButton) {
        modalButton.textContent = this.sendingTextValue;
        modalButton.disabled = true;
      }

      const response = await fetch("/form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        try {
          this._successToast.show();
        } catch (error) {}

        if (this._modalServerErrorEl) {
          this._modalServerErrorEl.classList.add("d-none");
          this._modalServerErrorEl.textContent = "";
        }
        if (this._modalInstance) this._modalInstance.hide();
        return;
      }

      let serverMessage = `${this.errorSendLabelValue} (код ${response.status})`;
      try {
        const body = await response.json();
        if (body?.error || body?.message) serverMessage = body.error || body.message;
      } catch (error) {}

      if (response.status === 400 && serverMessage.toLowerCase().includes("phone")) {
        if (this._modalPhoneErrorEl) {
          this._modalPhoneErrorEl.textContent = serverMessage;
          this._modalPhoneErrorEl.classList.remove("d-none");
        }
        if (this._modalPhoneInput) this._modalPhoneInput.classList.add("is-invalid");
      } else if (this._modalServerErrorEl) {
        this._modalServerErrorEl.textContent = serverMessage;
        this._modalServerErrorEl.classList.remove("d-none");
      }

      this._showErrorToast(serverMessage);
    } catch (error) {
      const message = error?.message
        ? `${this.errorNetworkLabelValue}: ${error.message}`
        : this.errorUnknownLabelValue;

      if (this._modalServerErrorEl) {
        this._modalServerErrorEl.textContent = message;
        this._modalServerErrorEl.classList.remove("d-none");
      }

      this._showErrorToast(message);
    } finally {
      if (mainButton) {
        mainButton.textContent = originalMainText;
        mainButton.disabled = false;
      }
      if (modalButton) {
        modalButton.textContent = originalModalText;
        modalButton.disabled = false;
      }

      this._isSending = false;
      this._updateModalSubmitState();
      this.refresh();
    }
  }

  _showErrorToast(message) {
    try {
      const body = this.element.querySelector(".error_msg .toast-body");
      if (body) {
        body.innerHTML = `<i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i> ${message}`;
      }
      this._errorToast.show();
    } catch (error) {
      try {
        this._errorToast.show();
      } catch (toastError) {}
    }
  }

  _renderInvoiceForPrint(snapshot) {
    this._cleanupInvoicePrint();

    const shell = document.createElement("div");
    shell.className = "invoice-print-shell";
    shell.setAttribute("aria-hidden", "true");

    const template = this.element.querySelector("#invoice-template");
    if (!template) return;

    const invoice = template.content.firstElementChild.cloneNode(true);
    const rowsHost = invoice.querySelector(".invoice-rows");
    const totalHost = invoice.querySelector(".invoice-total");
    const numberHost = invoice.querySelector(".invoice-no");
    const dateHost = invoice.querySelector(".invoice-date");

    if (numberHost) numberHost.textContent = this._formatInvoiceNumber();
    if (dateHost) dateHost.textContent = this._formatInvoiceDate();

    if (rowsHost) {
      rowsHost.innerHTML = snapshot.items
        .map((item, index) => this._buildInvoiceRowHtml(snapshot, item, snapshot.quote.rows[index], index))
        .join("");
    }

    if (totalHost) {
      totalHost.textContent = `${PriceCalculator.formatAmount(snapshot.quote.total)}\u2009${snapshot.quote.symbol}`;
    }

    shell.appendChild(invoice);
    document.body.appendChild(shell);
    this._printShell = shell;
  }

  _buildInvoiceRowHtml(snapshot, item, row, _index) {
    if (!item || !row) return "";

    const areaText = PriceCalculator.formatNumber(item.area, { maximumFractionDigits: 2 });
    const rateText = PriceCalculator.formatNumber(row.unitRate, { maximumFractionDigits: 3 });
    const totalText = `${PriceCalculator.formatAmount(row.amount)}\u2009${snapshot.quote.symbol}`;

    return `
      <tr>
        <td class="col-position">
          <span class="invoice-row-label">${this._escapeHtml(item.label)}</span>
        </td>
        <td class="col-area">${areaText}</td>
        <td class="col-price">${rateText}\u2009${snapshot.quote.symbol}</td>
        <td class="col-total">${totalText}</td>
      </tr>
    `;
  }

  _formatInvoiceNumber() {
    const now = new Date();
    const datePart = now.toISOString().slice(0, 10).replaceAll("-", "");
    const timePart = String(now.getHours()).padStart(2, "0") + String(now.getMinutes()).padStart(2, "0");
    return `HD-${datePart}-${timePart}`;
  }

  _formatInvoiceDate() {
    return new Intl.DateTimeFormat(this._getInvoiceLocale(), {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date());
  }

  _getInvoiceLocale() {
    const htmlLang = document.documentElement?.lang || "";
    const normalized = htmlLang.toLowerCase();

    if (!normalized) return navigator.language || "en-GB";
    if (normalized === "ua") return "uk-UA";

    return normalized;
  }

  _escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  _armInvoicePrintCleanup() {
    if (this._afterPrintHandler) return;

    this._afterPrintHandler = () => {
      this._cleanupInvoicePrint();
    };

    window.addEventListener("afterprint", this._afterPrintHandler, { once: true });
  }

  _cleanupInvoicePrint() {
    if (this._printShell) {
      this._printShell.remove();
      this._printShell = null;
    }

    if (this._previousDocumentTitle) {
      document.title = this._previousDocumentTitle;
      this._previousDocumentTitle = null;
    }

    if (this._afterPrintHandler) {
      window.removeEventListener("afterprint", this._afterPrintHandler);
      this._afterPrintHandler = null;
    }
  }

  _setInvoiceDocumentTitle() {
    if (!this._previousDocumentTitle) {
      this._previousDocumentTitle = document.title;
    }

    document.title = "Конструктор проекту | Hall Design";
  }

  _closeHelpTooltips() {
    this.element.querySelectorAll(".cstr-help[aria-expanded='true']").forEach((trigger) => {
      this._collapseHelpTrigger(trigger);
    });
  }

  _collapseHelpTrigger(trigger) {
    if (!trigger) return;
    trigger.setAttribute("aria-expanded", "false");
  }
}
